import React, { useState, useEffect } from 'react';
import { api } from '@/utils/api';
import { BottomNav, TopBar } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const AdminSlabs = ({ user }) => {
  const [slabs, setSlabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    role: 'Rep',
    city: '',
    quarter: 'Q1-2026',
    min_sales: '',
    max_sales: '',
    payout_percentage: '',
  });

  useEffect(() => {
    fetchSlabs();
  }, []);

  const fetchSlabs = async () => {
    setLoading(true);
    try {
      const response = await api.getSlabs();
      setSlabs(response.data);
    } catch (error) {
      toast.error('Failed to load slabs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.createSlab({
        ...formData,
        min_sales: parseFloat(formData.min_sales),
        max_sales: formData.max_sales ? parseFloat(formData.max_sales) : null,
        payout_percentage: parseFloat(formData.payout_percentage),
      });
      toast.success('Slab created successfully');
      setDialogOpen(false);
      setFormData({
        role: 'Rep',
        city: '',
        quarter: 'Q1-2026',
        min_sales: '',
        max_sales: '',
        payout_percentage: '',
      });
      fetchSlabs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create slab');
    }
  };

  const handleDelete = async (slabId) => {
    if (!window.confirm('Are you sure you want to delete this slab?')) return;
    
    try {
      await api.deleteSlab(slabId);
      toast.success('Slab deleted successfully');
      fetchSlabs();
    } catch (error) {
      toast.error('Failed to delete slab');
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
    return `₹${amount.toLocaleString()}`;
  };

  // Group slabs by role and quarter
  const groupedSlabs = slabs.reduce((acc, slab) => {
    const key = `${slab.role}-${slab.quarter}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(slab);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-slate-50 pb-nav">
      <TopBar title="Incentive Slabs Management" subtitle="Manage payout slabs" user={user} />

      <div className="p-4 md:p-8 space-y-6">
        {/* Create Button */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-slab-button" className="w-full md:w-auto">
              <Plus size={16} className="mr-2" />
              Create New Slab
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Incentive Slab</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="role">Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="slab-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Rep">Rep</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="quarter">Quarter</Label>
                <Select value={formData.quarter} onValueChange={(value) => setFormData({ ...formData, quarter: value })}>
                  <SelectTrigger data-testid="slab-quarter-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Q1-2026">Q1 2026</SelectItem>
                    <SelectItem value="Q2-2026">Q2 2026</SelectItem>
                    <SelectItem value="Q3-2026">Q3 2026</SelectItem>
                    <SelectItem value="Q4-2026">Q4 2026</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="city">City (optional)</Label>
                <Input
                  id="city"
                  data-testid="slab-city-input"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Mumbai"
                />
              </div>

              <div>
                <Label htmlFor="min-sales">Min Sales (₹)</Label>
                <Input
                  id="min-sales"
                  data-testid="slab-min-sales-input"
                  type="number"
                  value={formData.min_sales}
                  onChange={(e) => setFormData({ ...formData, min_sales: e.target.value })}
                  placeholder="10000000"
                  required
                />
              </div>

              <div>
                <Label htmlFor="max-sales">Max Sales (₹) - Optional</Label>
                <Input
                  id="max-sales"
                  data-testid="slab-max-sales-input"
                  type="number"
                  value={formData.max_sales}
                  onChange={(e) => setFormData({ ...formData, max_sales: e.target.value })}
                  placeholder="20000000"
                />
              </div>

              <div>
                <Label htmlFor="payout-percentage">Payout Percentage (%)</Label>
                <Input
                  id="payout-percentage"
                  data-testid="slab-payout-input"
                  type="number"
                  step="0.1"
                  value={formData.payout_percentage}
                  onChange={(e) => setFormData({ ...formData, payout_percentage: e.target.value })}
                  placeholder="3.0"
                  required
                />
              </div>

              <Button data-testid="submit-slab-button" type="submit" className="w-full">
                Create Slab
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Slabs List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading slabs...</p>
          </div>
        ) : Object.keys(groupedSlabs).length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-slate-500">No slabs created yet</p>
          </Card>
        ) : (
          <div data-testid="slabs-list" className="space-y-6">
            {Object.entries(groupedSlabs).map(([key, slabGroup]) => (
              <Card key={key} className="p-6">
                <h3 className="text-lg font-barlow font-bold uppercase mb-4">
                  {slabGroup[0].role} - {slabGroup[0].quarter}
                  {slabGroup[0].city && ` - ${slabGroup[0].city}`}
                </h3>
                <div className="space-y-2">
                  {slabGroup.map((slab) => (
                    <div
                      key={slab.slab_id}
                      data-testid={`slab-${slab.slab_id}`}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-slate-600">Range:</span>
                          <span className="font-mono font-semibold">
                            {formatCurrency(slab.min_sales)}
                            {slab.max_sales ? ` - ${formatCurrency(slab.max_sales)}` : '+'}
                          </span>
                          <span className="text-slate-400">|</span>
                          <span className="text-slate-600">Payout:</span>
                          <span className="font-bold text-emerald-600">
                            {slab.fixed_amount !== null && slab.fixed_amount !== undefined 
                              ? `₹${slab.fixed_amount.toLocaleString()}`
                              : `${slab.payout_percentage}%`
                            }
                            {slab.per_unit && ` per ${formatCurrency(slab.per_unit)}`}
                          </span>
                        </div>
                      </div>
                      <Button
                        data-testid={`delete-slab-${slab.slab_id}`}
                        onClick={() => handleDelete(slab.slab_id)}
                        variant="ghost"
                        size="sm"
                        className="text-[#5B3DF5] hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <BottomNav user={user} />
    </div>
  );
};

export default AdminSlabs;
